import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
// import Textarea from '../../components/common/Textarea';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import contestService from '../../api/services/contestService';
import categoryService from '../../api/services/categoryService';
import { handleApiError, formatDate, formatCurrency, getContestStatusColor } from '../../utils/helpers';
import { getImageUrl } from '../../utils/imageHelper';
// import Textarea from '../../components/common/Textarea';

const ContestDetailsPage = () => {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [contest, setContest] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    fetchContestDetails();
  }, [contestId]);

  const fetchContestDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch contest details
      const contestResponse = await contestService.getContestById(contestId);
      
      if (contestResponse.success) {
        setContest(contestResponse.data);
      }

      // Fetch categories for this contest
      const categoriesResponse = await categoryService.getContestCategories(contestId);
      
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data || []);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryModalOpen(true);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCategory = async () => {
    try {
      const response = await categoryService.deleteCategory(categoryToDelete.id);
      
      if (response.success) {
        success('Category deleted successfully!');
        fetchContestDetails();
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  const handleActivateContest = async () => {
    try {
      const response = await contestService.updateContest(contestId, { status: 'ACTIVE' });
      
      if (response.success) {
        success('Contest activated successfully!');
        fetchContestDetails();
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  // const handleTogglePause = async () => {
  //   try {
  //     const response = await contestService.togglePause(contestId);
      
  //     if (response.success) {
  //       success(`Contest ${contest.status === 'ACTIVE' ? 'paused' : 'resumed'} successfully!`);
  //       fetchContestDetails();
  //     }
  //   } catch (err) {
  //     showError(handleApiError(err));
  //   }
  // };

 const handleTogglePause = async () => {
  try {
    const response = await contestService.togglePause(contestId);
    
    if (response.success) {
      success(`Contest ${contest.isPaused ? 'resumed' : 'paused'} successfully!`);
      await fetchContestDetails();
    }
  } catch (err) {
    showError(handleApiError(err));
  }
};

  const handleToggleResults = async () => {
    try {
      const response = await contestService.toggleResults(contestId);
      
      if (response.success) {
        success(`Results ${contest.showResults ? 'hidden' : 'shown'} successfully!`);
        fetchContestDetails();
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  // Get image URL helper
  // const getImageUrl = (path) => {
  //   if (!path) return null;
  //   if (path.startsWith('http')) return path;
  //   const normalizedPath = path.replace(/\\/g, '/');
  //   const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
  //   return `${BASE_URL}/${normalizedPath}`;
  // };

  if (loading) {
    return (
      <DashboardLayout title="Contest Details">
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading contest details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Contest Details">
        <ErrorMessage message={error} onRetry={fetchContestDetails} />
      </DashboardLayout>
    );
  }

  if (!contest) {
    return (
      <DashboardLayout title="Contest Details">
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">Contest not found</p>
            <Button variant="outline" onClick={() => navigate('/admin/contests')} className="mt-4">
              Back to Contests
            </Button>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={contest.title}
      subtitle="Manage categories and contestants"
    >
      {/* Back Button & Actions - Mobile Responsive */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate('/admin/contests')}
          className="w-full sm:w-auto"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Contests
        </Button>

        {/* Contest Action Buttons - Mobile Responsive */}
        {/* <div className="flex flex-wrap items-center gap-2">
          {contest.status === 'DRAFT' && (
            <Button 
              variant="success" 
              size="sm" 
              onClick={handleActivateContest}
              className="flex-1 sm:flex-none"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden sm:inline">Activate Contest</span>
              <span className="sm:hidden">Activate</span>
            </Button>
          )}
          
          {(contest.status === 'ACTIVE' || contest.status === 'PAUSED') && (
            <Button
              variant={contest.status === 'ACTIVE' ? 'warning' : 'success'}
              size="sm"
              onClick={handleTogglePause}
              className="flex-1 sm:flex-none"
            >
              {contest.status === 'ACTIVE' ? (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Pause
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Resume
                </>
              )}
            </Button>
          )}

          <Button
            variant={contest.showResults ? 'primary' : 'outline'}
            size="sm"
            onClick={handleToggleResults}
            className="flex-1 sm:flex-none"
          >
            {contest.showResults ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
                <span className="hidden sm:inline">Hide Results</span>
                <span className="sm:hidden">Hide</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="hidden sm:inline">Show Results</span>
                <span className="sm:hidden">Show</span>
              </>
            )}
          </Button>
        </div> */}
        {/* Contest Action Buttons - Mobile Responsive */}
<div className="flex flex-wrap items-center gap-2">
  {contest.status === 'DRAFT' && (
    <Button 
      variant="success" 
      size="sm" 
      onClick={handleActivateContest}
      className="flex-1 sm:flex-none"
    >
      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="hidden sm:inline">Activate Contest</span>
      <span className="sm:hidden">Activate</span>
    </Button>
  )}
  
  {contest.status === 'ACTIVE' && (
    <Button
      variant={contest.isPaused ? 'success' : 'warning'}
      size="sm"
      onClick={handleTogglePause}
      className="flex-1 sm:flex-none"
    >
      {contest.isPaused ? (
        <>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Resume
        </>
      ) : (
        <>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pause
        </>
      )}
    </Button>
  )}

  <Button
    variant={contest.showResults ? 'primary' : 'outline'}
    size="sm"
    onClick={handleToggleResults}
    className="flex-1 sm:flex-none"
  >
    {contest.showResults ? (
      <>
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
        <span className="hidden sm:inline">Hide Results</span>
        <span className="sm:hidden">Hide</span>
      </>
    ) : (
      <>
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <span className="hidden sm:inline">Show Results</span>
        <span className="sm:hidden">Show</span>
      </>
    )}
  </Button>
</div>
      </div>

      {/* Contest Overview Card - Mobile Responsive */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          {/* Banner - Mobile Responsive */}
          {contest.banner ? (
            <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden border-4 border-gray-100">
              <img
                src={getImageUrl(contest.banner)}
                alt={contest.title}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center border-4 border-gray-100">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          )}

          {/* Contest Details */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-1 truncate">
                  {contest.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {contest.host?.name || 'Unknown Host'}
                </p>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getContestStatusColor(contest.status)}`}>
                {contest.status}
              </span>
            </div>

            <p className="text-gray-700 text-sm mb-4 line-clamp-2">
              {contest.description}
            </p>

            {/* Stats Grid - Mobile Responsive */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Categories</p>
                <p className="text-lg sm:text-xl font-semibold text-gray-900">
                  {contest._count?.categories || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Contestants</p>
                <p className="text-lg sm:text-xl font-semibold text-gray-900">
                  {contest._count?.contestants || 0}
                </p>
              </div>
              {/* <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Total Votes</p>
                <p className="text-lg sm:text-xl font-semibold text-gray-900">
                  {contest._count?.votes || 0}
                </p>
              </div> */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Vote Price</p>
                <p className="text-lg sm:text-xl font-semibold text-gray-900">
                  {formatCurrency(contest.votePrice)}
                </p>
              </div>
            </div>

            {/* Dates - Mobile Responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 mt-4">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="truncate">Start: {formatDate(contest.startDate)}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="truncate">End: {formatDate(contest.endDate)}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Categories Section */}
      <div>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
            <p className="text-sm text-gray-600">
              {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}
            </p>
          </div>
          <Button 
            variant="primary" 
            onClick={handleAddCategory}
            className="w-full sm:w-auto"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Category
          </Button>
        </div>

        {categories.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                No categories yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first category
              </p>
              <div className="mt-6">
                <Button variant="primary" onClick={handleAddCategory}>
                  Add Category
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                onEdit={() => handleEditCategory(category)}
                onDelete={() => handleDeleteClick(category)}
                contestId={contestId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Category Modal */}
      <CategoryFormModal
        isOpen={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setEditingCategory(null);
        }}
        onSuccess={() => {
          fetchContestDetails();
          setCategoryModalOpen(false);
          setEditingCategory(null);
        }}
        contestId={contestId}
        category={editingCategory}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteCategory}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryToDelete?.name}"? This will also delete all contestants in this category.`}
        confirmText="Delete"
        variant="danger"
      />
    </DashboardLayout>
  );
};

// Category Card Component - Mobile Responsive
const CategoryCard = ({ category, onEdit, onDelete, contestId }) => {
  const navigate = useNavigate();

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">
            {category.name}
          </h4>
          <p className="text-sm text-gray-600 line-clamp-2">{category.description}</p>
        </div>
        {category.order !== undefined && (
          <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium flex-shrink-0">
            #{category.order}
          </span>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 mb-4">
        <div className="flex items-center">
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span>{category._count?.contestants || 0} Contestants</span>
        </div>
        {/* <div className="flex items-center">
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span>{category._count?.votes || 0} Votes</span>
        </div> */}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate(`/admin/contests/${contestId}/categories/${category.id}`)}
          className="flex-1"
        >
          Manage
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onEdit}
          className="flex-1 sm:flex-none"
        >
          Edit
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onDelete}
          className="flex-1 sm:flex-none"
        >
          Delete
        </Button>
      </div>
    </Card>
  );
};

// Category Form Modal Component - Mobile Responsive
const CategoryFormModal = ({ isOpen, onClose, onSuccess, contestId, category }) => {
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: '0'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        order: category.order?.toString() || '0'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        order: '0'
      });
    }
  }, [category, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Category name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const categoryData = {
        contestId,
        name: formData.name.trim(),
        description: formData.description.trim(),
        order: parseInt(formData.order) || 0
      };

      const response = category
        ? await categoryService.updateCategory(category.id, categoryData)
        : await categoryService.createCategory(categoryData);

      if (response.success) {
        success(`Category ${category ? 'updated' : 'created'} successfully!`);
        onSuccess();
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? 'Edit Category' : 'Add Category'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Category Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Best Male Artist"
          error={errors.name}
          required
        />

        <Input
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Vote for the best male artist"
          error={errors.description}
          rows={3}
          required
        />

        <Input
          label="Display Order"
          type="number"
          name="order"
          value={formData.order}
          onChange={handleChange}
          placeholder="0"
          min="0"
          error={errors.order}
        />

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="flex-1 w-full sm:w-auto"
          >
            {category ? 'Update' : 'Create'} Category
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ContestDetailsPage;